/**
 * 業務（Business）の入力スキーマ — 8 欄（NE §1-attrs の Business）と、省いてよい受け入れ条件。
 *
 * 今の業務は 2 欄（ドメインと自由文の `logic[]`）＋ 名前の作り ＋ 説明だった。作り直しでは
 * 業務がハブになり、「誰が（アクター）・どの束で（ドメイン）・何を受け取り・いつ実行し・
 * どの業務ルールで決め・何を出し・どう終わるか」を持つ。`Operation` が間に挟まらなくなる
 * （実測で 1 業務 = 1 アクター、1 部品 = 1 業務）。
 *
 * **業務ロジックは業務ルールの集合で、順序も分岐の先も持たない。** 業務ルールの間の依存は、
 * 業務ルールの入力が別の業務ルールの出力かどうかから導出する（READS_RULE）。依存は書かせず、
 * 環・読まれない出力・宙吊りの入力はその依存に対してリリースゲートが見る。
 *
 * 同じ業務ルールを集合に 2 度書いた形はここでは止めない。書込の形の検査が止め、復元が直に
 * 入れた行は監査が形の不備として出す（ここで止めると、その 1 件でグラフ全体の読み分けが落ちる）。
 *
 * `rules` と `endings` の「1 件以上」を書込時に止めないのは、復元のときに業務 101 件を業務
 * ルールより先に戻すためである。0 件はリリースゲートの違反として出す。
 *
 * **結末の行はどこへ行くかを持たない。** どこへ行くかは業務の性質ではなく、その業務を使う
 * フローごとに違うので、フローの枝の宛先の型が持つ（NE §1-attrs の Business・Flow）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務（Business）の zod 入力スキーマ。アクター・入力・実行の時点・業務ルールの集合・出力・結末の表
 * @graph-connects none
 */
import { z } from 'zod';
import { businessAcceptanceSchema } from './business-acceptance.js';
import { optionalSpecElementId, specElementId } from './ids.js';
/**
 * 業務が出す件数 4 種（NE §1-attrs）。
 *
 * - `one` 1 件 / `many` n 件 / `all` 全件 / `none` 無し
 *
 * 今は規則の文の中に埋まっている（「1 名だけ返す」）。欄にすると、次の業務の入力と突き
 * 合わせられる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務が出す件数（1 件 / n 件 / 全件 / 無し）の allowlist
 * @graph-connects none
 */
export const BUSINESS_OUTPUT_COUNTS = ['one', 'many', 'all', 'none'];
/**
 * 到達の印 — その結末で立つ（プロパティ, 値）。どちらも用語の id。
 *
 * 今の規則が実装の状態として文に書いていたもの（「選定中の印を付ける」）の置き場である。
 * 業務ルールにはしない。プロパティの取りうる値に値が入っているかはリリースゲートが見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務の結末で立つ到達の印（プロパティと値の用語）の入力スキーマ
 * @graph-connects none
 */
export const reachedMarkSchema = z.strictObject({
    property: specElementId(),
    value: specElementId(),
});
/**
 * 結末の 1 行（結果の値, 到達の印）。行は結果の取りうる値ぶん並ぶ。
 *
 * 行は局所の番号を持たず、**結果の値（用語の id）がそのまま行の鍵になる**。フローの枝は
 * 結果の値で結末を指す（API §3.4b）。
 *
 * `reachedMark` に値が入るのは、その結末で立つ印があるときだけである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務の結末 1 行（結果の値・到達の印）の入力スキーマ
 * @graph-connects none
 */
export const endingSchema = z.strictObject({
    result: specElementId(),
    reachedMark: reachedMarkSchema.optional(),
});
/**
 * 結末の表。同じ結果の値の行を 2 つ書くと拒否する。
 *
 * 結果の値が行の鍵なので、重なるとフローの枝がどちらの行を指すのか決まらず、案件の差分でも
 * 行を対応づけられない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務の結末の表（結果の値が行の鍵で重複しない）の入力スキーマ
 * @graph-connects none
 */
const endingsSchema = z
    .array(endingSchema)
    .refine((endings) => new Set(endings.map((ending) => ending.result)).size === endings.length, {
    message: '結末の表に同じ結果の値の行が 2 つある（結果の値は行の鍵）',
});
/**
 * 業務が出力の件数によらず持つ欄。
 *
 * `acceptance`（受け入れ条件）は省いてよい。業務が何を大事にし、なぜそうするかをルールの並びで
 * 持つ（`business-acceptance.ts`）。業務ルールとの突き合わせは形の検査が見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務（Business）が出力の件数によらず共通に持つ欄（目的語・動詞・主体・区分・入力・業務ルールの集合・結末の表）の zod 形
 * @graph-connects none
 */
const businessCommonShape = {
    id: optionalSpecElementId(),
    subject: specElementId(),
    verb: specElementId(),
    actor: specElementId(),
    domain: specElementId(),
    inputs: z.array(specElementId()),
    executedAt: specElementId().optional(),
    rules: z.array(specElementId()),
    endings: endingsSchema,
    acceptance: businessAcceptanceSchema.optional(),
};
/**
 * 何かを出す業務（1 件 / n 件 / 全件）。出すものの区分（クラスの用語）が必須になる。
 *
 * @param outputCount 出す件数
 * @returns その件数の業務の zod 入力スキーマ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 何かを出す業務の zod 入力スキーマを件数ごとに組み立てる
 * @graph-connects none
 */
function producingBusinessSchema(outputCount) {
    return z.strictObject({
        ...businessCommonShape,
        outputCount: z.literal(outputCount),
        outputClass: specElementId(),
    });
}
/**
 * 何も出さない業務。出すものの区分は**書けない**。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 何も出さない業務の zod 入力スキーマ
 * @graph-connects none
 */
export const nonProducingBusinessSchema = z.strictObject({
    ...businessCommonShape,
    outputCount: z.literal('none'),
});
/**
 * 業務（Business）。出す件数で枝を選ぶ判別ユニオン 4 枝。
 *
 * `executedAt` に値が入るのは、日付・時刻を見る業務だけである（引き当て実行日時）。今は
 * 規則の文の中にある（「実行した日を基準に」）。
 *
 * `inputs` / `rules` / `endings` は空の並びを受け付ける。空を止めないのは
 * 復元の順序のためで、中身の不足はリリースゲートが数える。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 業務（Business）の zod 入力スキーマ（出す件数 4 種の判別ユニオン）
 * @graph-connects none
 */
export const businessElementSchema = z.discriminatedUnion('outputCount', [
    producingBusinessSchema('one'),
    producingBusinessSchema('many'),
    producingBusinessSchema('all'),
    nonProducingBusinessSchema,
]);
//# sourceMappingURL=business.js.map